import React from 'react';
import Text from '@zg-rentals/multibrand-ui/lib/Text';
import Button from '@zg-rentals/multibrand-ui/lib/Button';
import SpacerRow from '@zg-rentals/multibrand-ui/lib/SpacerRow';
import Label from '@zg-rentals/multibrand-ui/lib/Label';
import Select from '@zg-rentals/multibrand-ui/lib/Select';
import Checkbox from '@zg-rentals/multibrand-ui/lib/Checkbox';
import { formatDate } from '@common/paymentsUtils/dateUtils';
import Section from '@rental-manager/components/Section';
import HelpToolTip from '@rental-manager/pages/Onboarding/HelpToolTip';
import FaqComponent from '@common/components/FaqComponent';
import SpinnerContainer from '@common/components/SpinnerContainer';
import FaqConstants from '@rental-manager/constants/FaqConstants';
import { isStartDateTooSoon, makeDueDates, STORE_DATE_FORMAT } from '@common/paymentsUtils/selectOptionsBuilder';
import type { EndDatesInterface, StartDatesInterface } from '@common/types';
import { TERM_LENGTH_END_DATE_OPTIONS_COUNT } from '@rental-manager/constants/PaymentConstants';

type PaymentSettings = {
  firstDueDate: Date | string;
  monthToMonth: boolean;
  numberOfPayments?: number;
  paymentEndDate?: string;
};

type Props = {
  today: Date;
  firstDueDate: Date | string;
  numberOfPayments: number;
  monthToMonth: boolean;
  paymentDayOfTheMonth: number;
  onSubmit: (paymentSettings: PaymentSettings) => void;
  editMode: boolean;
  onCancel: () => void;
};

type State = {
  startDates: Array<StartDatesInterface>;
  selectedStartDate: StartDatesInterface;
  selectedEndDate: EndDatesInterface;
  monthToMonth: boolean;
  isLoading: boolean;
};

class NewTenantTermLength extends React.Component<Props, State> {
  static defaultProps = {
    today: new Date(),
    firstDueDate: new Date(),
    numberOfPayments: 12,
    monthToMonth: false,
    paymentDayOfTheMonth: 1,
    onSubmit: (): void => {},
    editMode: false,
  };

  constructor(props: Props) {
    super(props);
    const { firstDueDate, paymentDayOfTheMonth, numberOfPayments, monthToMonth } = this.props;
    const startDates = makeDueDates(paymentDayOfTheMonth, firstDueDate, TERM_LENGTH_END_DATE_OPTIONS_COUNT);
    const [firstStartDate, secondStartDate] = startDates;
    let selectedStartDate = firstStartDate;
    if (secondStartDate.date === formatDate(firstDueDate, STORE_DATE_FORMAT)) {
      selectedStartDate = secondStartDate;
    }

    const selectedEndDate =
      selectedStartDate.endDates.find((endDate) => endDate.payments === numberOfPayments) ||
      selectedStartDate.endDates[0]; // fallback to first option
    this.state = {
      startDates,
      selectedStartDate,
      selectedEndDate,
      monthToMonth,
      isLoading: false,
    };
  }

  onChangeMonthToMonth = (): void => {
    this.setState({
      monthToMonth: !this.state.monthToMonth,
    });
  };

  onSubmit = (): Promise<void> => {
    const { onSubmit } = this.props;
    const { selectedStartDate, selectedEndDate, monthToMonth } = this.state;

    return Promise.resolve()
      .then(() => this.setState({ isLoading: true }))
      .then(() => {
        const paymentSettings: PaymentSettings = {
          firstDueDate: selectedStartDate.date,
          monthToMonth,
        };

        if (!monthToMonth) {
          paymentSettings.numberOfPayments = selectedEndDate.payments || 0;
          paymentSettings.paymentEndDate = selectedEndDate.date;
        }

        return onSubmit(paymentSettings);
      })
      .then((response: any) => {
        if (response.error) {
          this.setState({ isLoading: false });
        }
      });
  };

  onChangeStartDate = (e: any): void => {
    const { startDates } = this.state;
    const selectedStartDate = startDates[e.currentTarget.selectedIndex];

    this.setState({ selectedStartDate });
  };

  onChangeEndDate = (e: any): void => {
    const { selectedStartDate } = this.state;
    const selectedEndDate = selectedStartDate.endDates[e.currentTarget.selectedIndex];

    this.setState({ selectedEndDate });
  };

  // TODO: Warning: Use the `defaultValue` or `value` props on <select> instead of setting `selected` on <option>.
  renderStartDateOptions = (): React.ReactNode => {
    const { startDates } = this.state;

    return startDates.map(({ date, display }) => <option key={date}>{display}</option>);
  };

  renderEndDateOptions = (): React.ReactNode => {
    const { selectedStartDate, selectedEndDate } = this.state;

    return selectedStartDate.endDates.map(({ date, display, payments }) => (
      <option key={date} value={payments} selected={payments === selectedEndDate.payments}>
        {display}
      </option>
    ));
  };

  render(): React.ReactNode {
    const { editMode, paymentDayOfTheMonth, today } = this.props;
    const { isLoading, monthToMonth, selectedStartDate } = this.state;
    const { wasAdjusted, date: startDate } = selectedStartDate;

    const startDateOptions = this.renderStartDateOptions();
    const endDateOptions = this.renderEndDateOptions();
    const renterMayNotHaveTime = isStartDateTooSoon(startDate, today);
    const startDateLabel = paymentDayOfTheMonth === 1 ? 'First full month of rent' : 'First full rent payment starts';
    return (
      <>
        {isLoading && <SpinnerContainer />}
        <Section>
          <Text type="h3">How long will you be collecting rent?</Text>
        </Section>

        <Section>
          <Label className="flexDisplay">
            {startDateLabel}
            <HelpToolTip>
              Enter your tenant&apos;s monthly rent payment date. This may be different from the payment date for
              move-in costs, such as a security deposit.
            </HelpToolTip>
          </Label>
          <Select onChange={this.onChangeStartDate}>{startDateOptions}</Select>
          {renterMayNotHaveTime && (
            <Text type="body-small" lowlight className="TermLengthPage-StartDateSoon">
              Your renter may not have time to set up a payment before this start date. Their first payment may take a
              few additional days to reach you.
            </Text>
          )}

          {wasAdjusted && (
            <Text type="body-small" lowlight>
              This month has fewer than {paymentDayOfTheMonth} days, so we selected the the last day of the month by
              default.
            </Text>
          )}
        </Section>
        <Section>
          <Label className="flexDisplay">End date</Label>
          <Select disabled={monthToMonth} onChange={this.onChangeEndDate}>
            {endDateOptions}
          </Select>
        </Section>
        <Section>
          <Checkbox checked={monthToMonth} onChange={this.onChangeMonthToMonth} label="No end date/Month-to-month" />
        </Section>
        <Section>
          <SpacerRow>
            <FaqComponent.Landlord faqs={FaqConstants.ONBOARDING_V2.TERM_LENGTH_PAGE} />
          </SpacerRow>
        </Section>
        <Section>
          <Button type="primary" onClick={this.onSubmit} full>
            {editMode ? 'Update' : 'Continue'}
          </Button>
        </Section>
        {editMode && (
          <Section>
            <Button type="tertiary" full data-statekey="Cancel" onClick={this.props.onCancel}>
              Cancel
            </Button>
          </Section>
        )}
      </>
    );
  }
}

export default NewTenantTermLength;
